import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import {
  FiShoppingCart,
  FiUser,
  FiMoon,
  FiSun,
  FiMenu,
  FiX
} from "react-icons/fi";

const Navbar = () => {

  const [isOpen, setIsOpen] = useState(false);

  // SAFE context usage
  const cart = useCart();
  const cartCount = cart?.cartCount || 0;

  const themeContext = useTheme();
  const theme = themeContext?.theme || "light";
  const toggleTheme = themeContext?.toggleTheme;

  const auth = useAuth();
  const user = auth?.user;
  const logout = auth?.logout;

  const navigate = useNavigate();

  const handleLogout = () => {
    if (logout) logout();
    navigate("/");
  };

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Collections", path: "/shop" },
    { name: "Bridal Lehenga", path: "/shop?category=bridal-lehenga" },
    { name: "Party Wear", path: "/shop?category=party-wear" },
    { name: "Wedding Gowns", path: "/shop?category=wedding-gowns" }
  ];

  return (
    <motion.nav
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      className="fixed top-0 w-full z-50 backdrop-blur-xl bg-white/70 dark:bg-gray-900/70 border-b border-gray-200 dark:border-gray-700 shadow-lg"
    >

      <div className="max-w-7xl mx-auto px-6">

        <div className="flex justify-between items-center py-4">

          {/* Logo */}

          <Link
            to="/"
            className="text-3xl font-bold tracking-wider bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent"
          >
            Ms Bridal Wear
          </Link>

          {/* Desktop Menu */}

          <div className="hidden md:flex items-center gap-8">

            {navLinks.map((link) => (

              <NavLink
                key={link.name}
                to={link.path}
                className={({ isActive }) =>
                  `relative font-medium transition ${
                    isActive
                      ? "text-yellow-500"
                      : "hover:text-yellow-500"
                  }`
                }
              >
                {link.name}
              </NavLink>

            ))}

            {/* Theme */}

            <button
              onClick={toggleTheme}
              className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-800 transition"
            >
              {theme === "dark" ? <FiSun /> : <FiMoon />}
            </button>

            {/* Cart */}

            <Link to="/cart" className="relative">

              <FiShoppingCart size={22} />

              {cartCount > 0 && (

                <span className="absolute -top-2 -right-2 bg-yellow-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">

                  {cartCount}

                </span>

              )}

            </Link>

            {/* User */}

            {user ? (

              <div className="flex items-center gap-3">

                <FiUser />

                <span className="text-sm">{user?.name}</span>

                <button
                  onClick={handleLogout}
                  className="text-red-500 hover:text-red-700 text-sm"
                >
                  Logout
                </button>

              </div>

            ) : (

              <Link
                to="/login"
                className="px-4 py-2 rounded-full bg-yellow-500 text-black font-medium hover:bg-yellow-400 transition"
              >
                Login
              </Link>

            )}

          </div>

          {/* Mobile button */}

          <button
            className="md:hidden"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <FiX size={26}/> : <FiMenu size={26}/>}
          </button>

        </div>

      </div>

      {/* Mobile Menu */}

      <AnimatePresence>

        {isOpen && (

          <motion.div
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            className="md:hidden bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700"
          >

            <div className="flex flex-col gap-4 p-6">

              {navLinks.map((link) => (

                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className="hover:text-yellow-500"
                >
                  {link.name}
                </Link>

              ))}

              {user ? (

                <button
                  onClick={() => {
                    handleLogout();
                    setIsOpen(false);
                  }}
                  className="text-red-500"
                >
                  Logout
                </button>

              ) : (

                <Link
                  to="/login"
                  onClick={() => setIsOpen(false)}
                >
                  Login
                </Link>

              )}

            </div>

          </motion.div>

        )}

      </AnimatePresence>

    </motion.nav>
  );
};

export default Navbar;